import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, BookOpen } from 'lucide-react';
import { StoriesAPI } from '../api';
import { normaliseStories } from '../hooks/useStories';
import { SmartImage } from './ArticleCard';

/**
 * Affiche 3 articles similaires (même catégorie), en excluant l'article courant.
 * Utilisé en bas de la page Article.
 */
const RelatedArticles = ({ currentSlug, categorySlug, limit = 3 }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!categorySlug) return;
    let cancelled = false;
    setLoading(true);
    StoriesAPI.list({ category: categorySlug, limit: limit + 5, order: 'desc' })
      .then((data) => {
        if (cancelled) return;
        const filtered = normaliseStories(data).filter((s) => s.slug !== currentSlug).slice(0, limit);
        setItems(filtered);
      })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [currentSlug, categorySlug, limit]);

  if (!loading && items.length === 0) return null;

  return (
    <section className="mt-14 pt-10 border-t border-[#e6ddd0]">
      <div className="flex items-center gap-3 mb-8">
        <BookOpen size={17} className="text-[#c9a961]" />
        <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-[#c9a961]">
          À lire ensuite
        </div>
        <div className="h-[1px] flex-1 bg-[#e6ddd0]" />
      </div>
      <h3
        className="text-[26px] md:text-[30px] font-bold text-[#1a1414] mb-8 italic"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
      >
        Ces histoires devraient aussi vous toucher
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {loading ? (
          Array.from({ length: limit }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-[16/10] bg-[#efe8de]" />
              <div className="h-3 w-20 bg-[#efe8de] mt-4" />
              <div className="h-6 w-full bg-[#efe8de] mt-3" />
              <div className="h-4 w-3/4 bg-[#efe8de] mt-2" />
            </div>
          ))
        ) : (
          items.map((p) => (
            <article key={p.id} className="group">
              <Link to={`/article/${p.slug}`} className="block relative aspect-[16/10] overflow-hidden bg-[#efe8de] mb-4">
                <SmartImage src={p.image} alt={p.title} />
              </Link>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#8b2c3f] font-bold">
                {p.category}
              </div>
              <h4
                className="mt-2 text-[19px] leading-tight font-bold text-[#1a1414] group-hover:text-[#8b2c3f] transition-colors line-clamp-3"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
              >
                <Link to={`/article/${p.slug}`}>{p.title}</Link>
              </h4>
              <div className="mt-3 flex items-center gap-3 text-[11px] text-[#7a716c]">
                <Clock size={11} />
                <span>{p.read_time || '10 min'}</span>
                <span className="text-[#c9a961]">•</span>
                <span>{p.date || ''}</span>
              </div>
            </article>
          ))
        )}
      </div>
    </section>
  );
};

export default RelatedArticles;
